/**
 * Filtro de patios de selección MÚLTIPLE, compartido por Tarjetas, Vehículos,
 * Grupos y Personal (y la regla de coincidencia de Accesos).
 *
 * Módulo puro a propósito, igual que `cardYards.ts`: las páginas importan
 * stores que arrastran `utils/api` → firebase, y en un worktree sin
 * `apps/v5/.env` eso revienta al colectar el test.
 */

/**
 * Valor de la opción "Sin patio asignado". No puede ser `"all"` ni `""`:
 * `HeaderComponent` traduce `"all"` a `""` en los filtros simples, y los dos se
 * leerían como "todos los patios". Es el mismo valor que ya usaba Tarjetas.
 */
export const NO_YARD_FILTER = "__none__";

/**
 * Si un elemento con `itemYardIds` pasa el filtro `selected`.
 *
 *   · `[]` (nada elegido) = todos los patios: pasa todo.
 *   · Pasa si comparte AL MENOS un patio con la selección — un vehículo de los
 *     patios A y B aparece al filtrar por A, por B o por A+C.
 *   · `NO_YARD_FILTER` dentro de la selección suma los elementos SIN ningún
 *     patio. Combinado con patios es una unión ("A o sin patio"), no una
 *     intersección, que es lo único que se puede leer en un combo de chips.
 */
export function matchesYardSelection(
  itemYardIds: readonly string[] | null | undefined,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const ids = itemYardIds ?? [];
  if (ids.length === 0) return selected.includes(NO_YARD_FILTER);
  return ids.some((id) => selected.includes(id));
}

/**
 * Quita de la selección lo que ya no se ofrece (un patio borrado, o uno que
 * salió del alcance del usuario) sin tocar `NO_YARD_FILTER`. Devuelve la MISMA
 * referencia si no hubo nada que quitar, para que sirva como dependencia de
 * `useMemo` sin recalcular en cada render.
 */
export function pruneYardSelection(
  selected: string[],
  offeredIds: readonly string[],
): string[] {
  const next = selected.filter(
    (id) => id === NO_YARD_FILTER || offeredIds.includes(id),
  );
  return next.length === selected.length ? selected : next;
}

/**
 * Qué patios ofrece el modal de Sincronizar, y en cuál arranca, según lo que
 * hay elegido en el filtro de selección múltiple.
 *
 * El modal sólo sabe sincronizar UN patio o "todos" (los de la lista que
 * recibe), y sincronizar ESCRIBE: crea inspecciones a partir del log de cada
 * controlador. Con varios patios elegidos, arrancar en "todos" sobre la lista
 * entera del alcance sincronizaba patios que el usuario no tenía en el filtro
 * — un supervisor de siete patios que filtraba dos disparaba los siete. Por eso
 * con dos o más la LISTA se recorta a la selección, y "todos" pasa a querer
 * decir "todos los elegidos".
 *
 *   · Nada elegido: todos los patios ofrecidos, arranca en "todos".
 *   · Uno: todos los ofrecidos, arranca en ése (se puede cambiar a mano).
 *   · Varios: sólo los elegidos, arranca en "todos" (esos).
 */
export function syncYardsForSelection<T extends { id: string }>(
  offered: readonly T[],
  selectedYardIds: readonly string[],
): { yards: T[]; defaultYardId: string } {
  if (selectedYardIds.length <= 1) {
    return { yards: [...offered], defaultYardId: selectedYardIds[0] || "" };
  }
  return {
    yards: offered.filter((y) => selectedYardIds.includes(y.id)),
    defaultYardId: "",
  };
}
